import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "./root.store";
import type { TTimelineFilterParams } from "@/services/timeline.service";
import type {
  TTimelineHolidayRegion,
  TTimelineItem,
  TTimelineResponse,
  TTimelineTimeOff,
  TTimelineUser,
} from "@/types/timeline-view";

export class TimelineViewStore {
  /** Last successfully loaded response. Kept visible while a new fetch is in
   *  flight so the grid can render skeleton rows from the previous groups. */
  response: TTimelineResponse | null = null;
  isLoading = false;
  error = false;

  /**
   * Bumped on every optimistic {@link setItemSchedule}. The render pipeline
   * (row-grouping → lane-assignment) is memoized on the `response` *reference*,
   * which an in-place date mutation does not change — so a dragged bar would
   * never re-lane and never visibly move. Consumers depend on `revision` to
   * force those memos to recompute on each optimistic update.
   */
  revision = 0;

  /** Monotonic fetch counter — responses from superseded fetches are dropped
   *  so an out-of-order resolution can't overwrite newer data (the window can
   *  refetch several times in quick succession while the user scrolls). */
  private fetchSeq = 0;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  get items(): TTimelineItem[] {
    return this.response?.items ?? [];
  }

  get users(): TTimelineUser[] {
    return this.response?.users ?? [];
  }

  get timeOff(): TTimelineTimeOff[] {
    return this.response?.time_off ?? [];
  }

  get holidays(): Record<string, TTimelineHolidayRegion> {
    return this.response?.holidays ?? {};
  }

  /**
   * Optimistically move an item to a new schedule so the grid re-renders live
   * (e.g. while a bar is being dragged). Mutating the item in `response.items`
   * keeps the existing object identity stable for everything except the dates,
   * and bumping {@link revision} lets the memoized render pipeline pick up the
   * move. Returns nothing; callers keep the previous schedule to revert on
   * failure.
   *
   * Dates and datetimes move together in ONE call because the two layouts read
   * different halves of the same move: the horizontal Gantt and the month grid
   * place a bar by `start`/`end`, while the hourly grid places a timed event by
   * its datetimes converted to the viewer's zone. A drag across day columns on
   * the hourly grid changes both, and applying them in two steps leaves a frame
   * where the bar sits on a day the item's own dates disagree with (NG-2041).
   *
   * Omit the datetimes for an item that has none to move — a task, or an
   * all-day event, whose bar is placed by its dates alone.
   */
  setItemSchedule(
    itemId: string,
    start: string,
    end: string,
    startDatetime?: string,
    endDatetime?: string,
  ): void {
    const item = this.response?.items.find((it) => it.id === itemId);
    if (!item) return;
    const datesMoved = item.start !== start || item.end !== end;
    const timesMoved =
      startDatetime !== undefined &&
      endDatetime !== undefined &&
      (item.start_datetime !== startDatetime || item.end_datetime !== endDatetime);
    if (!datesMoved && !timesMoved) return;
    item.start = start;
    item.end = end;
    if (timesMoved) {
      item.start_datetime = startDatetime;
      item.end_datetime = endDatetime;
    }
    this.revision += 1;
  }

  /**
   * Fetch timeline data. Global scope by default; pass `{ projectId }` to scope
   * to a single project (all assignees), or `{ workItemId }` to scope to a work
   * item's subtree (the event detail Timeline tab). All paths share the same
   * response shape and handling — the views are never mounted simultaneously, so
   * a single singleton store is safe. `filters` narrows the returned items
   * server-side (same params as the work-item list endpoints).
   */
  async fetch(
    slug: string,
    start: string,
    end: string,
    options?: { projectId?: string; workItemId?: string; filters?: TTimelineFilterParams },
  ): Promise<void> {
    this.isLoading = true;
    this.error = false;
    this.fetchSeq += 1;
    const requestId = this.fetchSeq;
    try {
      const { projectId, workItemId, filters } = options ?? {};
      let response;
      if (workItemId) {
        response = await this.rootStore.timelineService.getWorkItemTimeline(
          slug,
          workItemId,
          start,
          end,
          filters,
        );
      } else if (projectId) {
        response = await this.rootStore.timelineService.getProjectTimeline(
          slug,
          projectId,
          start,
          end,
          filters,
        );
      } else {
        response = await this.rootStore.timelineService.getTimeline(slug, start, end, filters);
      }
      runInAction(() => {
        if (requestId !== this.fetchSeq) return; // superseded by a newer fetch
        this.response = response;
      });
    } catch (error) {
      console.error("Timeline fetch failed", { slug, start, end, ...options, error });
      runInAction(() => {
        if (requestId !== this.fetchSeq) return;
        this.error = true;
      });
    } finally {
      runInAction(() => {
        // A superseded fetch must not clear the newer fetch's loading flag.
        if (requestId === this.fetchSeq) this.isLoading = false;
      });
    }
  }
}
